// src/hooks/useDashboardData.ts
import { useEffect, useState } from "react";

export interface ScheduleSlot {
  time: string;
  subject: string;
}

export interface DashboardData {
  schedule: ScheduleSlot[];
  recentActivity: { detail: string }[];
  performance: {
    learningStreak: string;
    averageScore: string;
  };
  crowdfunding: {
    status: string;
    appliedOn: string;
  }[];
  notifications: { message: string }[];
  recommendations: { subject: string; suggestion: string }[];
}

// Define the return type for clarity.
export interface UseDashboardDataResult extends DashboardData {
  loading: boolean;
  error: string | null;
}

export const useDashboardData = (): UseDashboardDataResult => {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    schedule: [],
    recentActivity: [],
    performance: {
      learningStreak: "",
      averageScore: "",
    },
    crowdfunding: [],
    notifications: [],
    recommendations: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchDashboardData = async () => {
      try {
        // Use the environment variable for the base URL or fallback to localhost.
        const baseUrl = process.env.REACT_APP_API_URL || "https://lavernaibackend.onrender.com/api";
        const dashboardUrl = `${baseUrl}/dashboard`;

        // Optionally include the auth token if available.
        const token = localStorage.getItem("authToken");

        const response = await fetch(dashboardUrl, {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            ...(token && { "Authorization": `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || "Failed to fetch dashboard data");
        }

        const data: DashboardData = await response.json();
        setDashboardData(data);
      } catch (err: any) {
        // Ignore abort errors
        if (err.name !== "AbortError") {
          setError(err.message || "An error occurred while fetching data.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();

    return () => {
      // Cancel the fetch when the component unmounts
      controller.abort();
    };
  }, []);

  return { ...dashboardData, loading, error };
};

export default useDashboardData;
